import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Anchor, Divider, Select, TextInput } from '@mantine/core';
import { Link } from 'react-router-dom';
import { useMediaQueryIndex } from '../../../helpers/hooks';
import AppFlexbox from '../../common/AppFlexbox';
import {
  ECOM_DISCOUNT_TYPE_ENUM,
  ECOM_PRODUCT_STATUS_ENUM
} from '../../../config/constants';
import AppStack from '../../common/AppStack';
import EditProductCard from '../products/EditProductCard';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import VendorCollectionProductSelectFormSection from '../vendors/VendorCollectionProductSelectFormSection';
import AppText from '../../common/AppText';
import VendorMediaSortableDropzone from '../media/VendorMediaSortableDropzone';
import AppTextEditor from '../../common/AppTextEditor';
import BundleDiscountEditControls from './BundleDiscountEditControls';
import PriceRangeLineItem from '../../common/PriceRangeLineItem';
import {
  calculateBundlePrice,
  calculateDiscountPercentage
} from '../../../helpers/format';
import AppCheckbox from '../../common/AppCheckbox';

const VendorFixedBundleEdit = ({
  isSubmitting,
  loading,
  formState,
  setFormState,
  productUrl
}) => {
  const { state: authState } = useContext(AuthContext);
  const { isLaptopOrSmaller } = useMediaQueryIndex();

  const productPrices = formState.ecomVendorBundleProducts.reduce(
    (r, c) => ({
      minPrice: r.minPrice + c.minPrice,
      maxPrice: r.maxPrice + c.maxPrice
    }),
    { minPrice: 0, maxPrice: 0 }
  );

  const bundleDiscount = formState.ecomVendorBundleDiscounts[0] || {
    fkEcomDiscountType: ECOM_DISCOUNT_TYPE_ENUM.NO_DISCOUNT,
    discountAmount: 0,
    sort: 1,
    minQuantity: 1
  };

  const minBundlePrice = calculateBundlePrice(
    productPrices.minPrice,
    bundleDiscount.fkEcomDiscountType,
    bundleDiscount.discountAmount
  );
  const maxBundlePrice = calculateBundlePrice(
    productPrices.maxPrice,
    bundleDiscount.fkEcomDiscountType,
    bundleDiscount.discountAmount
  );
  const discountPercentage = calculateDiscountPercentage(
    productPrices.maxPrice,
    maxBundlePrice,
    productPrices.maxPrice - maxBundlePrice
  );

  return (
    <AppFlexbox style={{ flexDirection: isLaptopOrSmaller ? 'column' : 'row' }}>
      <AppStack
        style={{
          flex: 2,
          gap: 16
        }}
      >
        <VendorCollectionProductSelectFormSection
          enableQuantityChange
          isSubmitting={isSubmitting}
          loading={loading}
          onChange={(products) => {
            setFormState({
              ...formState,
              ecomVendorBundleProducts: products
            });
          }}
          selectedProducts={formState.ecomVendorBundleProducts}
          showVendorName={authState.userData.isAdmin}
          subTitle="Select the product that will be sold together in this bundle."
          title="Included products"
        />

        <EditProductCard title="Discount">
          <BundleDiscountEditControls
            bundleDiscount={bundleDiscount}
            disabled={isSubmitting}
            maxPrice={productPrices.maxPrice}
            onChange={(updatedDiscount) =>
              setFormState({
                ...formState,
                ecomVendorBundleDiscounts: [updatedDiscount]
              })
            }
          />
        </EditProductCard>

        <EditProductCard
          title="Bundle details"
          titleAction={
            productUrl ? (
              <Anchor
                component={Link}
                style={{ fontSize: 14, color: 'dodgerblue' }}
                to={productUrl}
              >
                View product
              </Anchor>
            ) : null
          }
        >
          <AppStack>
            <TextInput
              disabled={isSubmitting}
              label="Title"
              onChange={(e) =>
                setFormState({
                  ...formState,
                  name: e.currentTarget.value
                })
              }
              required
              value={formState.name}
            />

            <AppStack style={{ gap: 5 }}>
              <AppTextEditor
                characterLimit={1250}
                disabled={isSubmitting}
                label="Description"
                onChange={(v) => {
                  setFormState({
                    ...formState,
                    description: v
                  });
                }}
                value={formState.description}
              />
            </AppStack>

            <AppStack style={{ gap: 5 }}>
              <VendorMediaSortableDropzone
                canChangeImages
                disabled={isSubmitting}
                fkEcomVendor={authState.pkEcomVendor}
                media={formState.ecomVendorProductMedia}
                onChange={(media) => {
                  setFormState({
                    ...formState,
                    ecomVendorProductMedia: media
                  });
                }}
                title={
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    Media
                  </AppText>
                }
              />
            </AppStack>
          </AppStack>
        </EditProductCard>
      </AppStack>

      <AppStack
        style={{ flex: 1, maxWidth: isLaptopOrSmaller ? 'unset' : 300 }}
      >
        <EditProductCard title="Status">
          <Select
            data={[
              { value: ECOM_PRODUCT_STATUS_ENUM.ACTIVE, label: 'Active' },
              { value: ECOM_PRODUCT_STATUS_ENUM.DRAFT, label: 'Draft' }
            ]}
            disabled={isSubmitting}
            onChange={(v) => {
              setFormState({
                ...formState,
                fkEcomProductStatus: v
              });
            }}
            value={formState.fkEcomProductStatus ?? ''}
          />

          {authState.userData.isAdmin && (
            <AppCheckbox
              checked={formState.isPrivate}
              description="When selected, this bundle product will be hidden from the public catalog but can still be added to any store"
              disabled={isSubmitting}
              label="Visible to SportsHeadz admins only"
              onChange={() => {
                setFormState({
                  ...formState,
                  isPrivate: !formState.isPrivate
                });
              }}
              styles={{ label: { fontWeight: 500 } }}
            />
          )}
        </EditProductCard>

        <EditProductCard title="Pricing">
          {formState.ecomVendorBundleProducts.length === 0 ? (
            <AppStack style={{ gap: 5 }}>
              <AppText
                style={{
                  fontSize: 14,
                  color: '#666',
                  fontStyle: 'italic',
                  textAlign: 'center'
                }}
              >
                No products selected yet, add products to preview bundle
                pricing.
              </AppText>
            </AppStack>
          ) : (
            <AppStack style={{ gap: 5 }}>
              <PriceRangeLineItem
                label="Original price"
                loading={loading}
                maxPrice={productPrices.maxPrice}
                minPrice={productPrices.minPrice}
              />

              <PriceRangeLineItem
                color={
                  productPrices.maxPrice - maxBundlePrice > 0
                    ? '#C40000'
                    : '#666'
                }
                label={
                  loading || discountPercentage <= 0
                    ? 'Discount'
                    : `Discount (${discountPercentage}%)`
                }
                loading={loading}
                maxPrice={(productPrices.maxPrice - maxBundlePrice) * -1}
                minPrice={(productPrices.minPrice - minBundlePrice) * -1}
              />

              <Divider />

              <PriceRangeLineItem
                label="Final price"
                loading={loading}
                maxPrice={maxBundlePrice}
                minPrice={minBundlePrice}
                weight={500}
              />
            </AppStack>
          )}
        </EditProductCard>
      </AppStack>
    </AppFlexbox>
  );
};

VendorFixedBundleEdit.propTypes = {
  formState: PropTypes.object.isRequired,
  setFormState: PropTypes.func.isRequired,
  ecomVendorBundle: PropTypes.object,
  isSubmitting: PropTypes.bool,
  loading: PropTypes.bool,
  productUrl: PropTypes.string
};

export default VendorFixedBundleEdit;
